import { useCreate, useInvalidate, useList } from "@refinedev/core";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents, DocumentInput } from "types/analytics";
import { useOrganization } from "hooks/useOrganization";
import { useWorkspace } from "hooks/useWorkspace/";
import {
  MediaProject,
  ProjectTag,
  ProjectInstanceSettingsRequest,
} from "pages/media/types";
import { ProjectType } from "../types";
import { isProjectHeyGenius, hasHeyGeniusDocTag } from "../helpers/helper";
import {
  getFileExtension,
  guessProjectTitleFromUrl,
  guessProjectTitleFromDocument,
} from "../../utils";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";

export const useProjectCreation = () => {
  const [searchParams] = useSearchParams();
  const { organization, organizationPreferences } = useOrganization({});
  const { workspaceId: globalWorkspaceId, defaultWorkspace } = useWorkspace();
  // For creation, fall back to default workspace when "All" is selected
  const workspaceId =
    searchParams.get("workspace_id") ??
    globalWorkspaceId ??
    defaultWorkspace?.id;
  const { trackOnboardingEvent: trackEvent } = useAnalytics();
  const { paywallFlow } = useSoftPaywall();

  const projectListResource = useMemo(() => {
    const base = `media/${organization?.id}/projects`;
    return base;
  }, [organization?.id]);

  // Get the total number of projects in the current organization for analytics
  // (not only created by the current user)
  // it can be useful to see when users reach their quotas per organization
  const { data: existingProjectsData } = useList<MediaProject>({
    resource: projectListResource,
    queryOptions: {
      enabled: Boolean(organization?.id),
    },
  });

  const { mutateAsync: create, isPending: isCreating } =
    useCreate<MediaProject>();
  const invalidate = useInvalidate();

  const createProject = async (
    filenames: string[],
    projectType: ProjectType = "new_video",
    tags: ProjectTag[] = [],
    detectedLanguage?: string | null,
    isFlashProject = false,
    savedSettingsId?: string | null,
    savedSettingsHasBrandKit?: boolean,
    extraMediaSettings?: Partial<ProjectInstanceSettingsRequest>,
    documentInput: DocumentInput = "file",
    organizationIdOverride?: string,
    /** Playbook whose add-on settings apply at creation (`playbook_id`). */
    playbookId?: string | null,
    /** Overrides the filename-derived title. */
    titleOverride?: string,
    description?: string
  ): Promise<MediaProject> => {
    const title = isProjectHeyGenius(projectType)
      ? guessProjectTitleFromUrl(filenames[0] ?? "")
      : guessProjectTitleFromDocument(filenames);

    // A playbook already carries these server-side, so sending ours would
    // override it — a playbook left on "auto" would get forced to a concrete
    // language (MED-7234). The language the user picks in Customize still
    // reaches the backend: it rides on the playbook itself.
    const mediaSettings = {
      ...(!isFlashProject &&
        !playbookId && {
          transitions:
            isProjectHeyGenius(projectType) || hasHeyGeniusDocTag(tags)
              ? "NoTransition"
              : "Basic",
        }),
      ...(savedSettingsId && {
        generate_brandkit: !savedSettingsHasBrandKit,
      }),
      ...extraMediaSettings,
    };

    const values = {
      title: titleOverride || title,
      organization_id: organizationIdOverride ?? organization?.id,
      type: isFlashProject ? "flash" : projectType,
      tags,
      workspace_id: workspaceId,
      saved_settings_id: savedSettingsId,
      ...(description && { description }),
      ...(playbookId && { playbook_id: playbookId }),
      ...(!playbookId && {
        storyboard_settings: {
          language: detectedLanguage || organizationPreferences?.language,
        },
      }),
      ...(Object.keys(mediaSettings).length > 0 && {
        media_settings: mediaSettings,
      }),
    };

    // Check if this is the first project created in the organization
    const isFirstProjectCreated = existingProjectsData?.total === 0;

    const data = await create({
      resource: "media/projects",
      values,
      successNotification: false,
    });

    // The create above only invalidates "media/projects"; the org-scoped list
    // (soft paywall count, project_number below) is a separate resource.
    // Awaited so callers navigate with a fresh count (MED-7488).
    await invalidate({ resource: projectListResource, invalidates: ["list"] });

    const document_format = isProjectHeyGenius(projectType)
      ? "url"
      : getFileExtension(filenames[0] ?? "");

    const eventProps = {
      project_id: data.data.id,
      document_format,
      project_type: isFlashProject ? "flash" : projectType,
      is_first_project: isFirstProjectCreated,
      document_input: documentInput,
      // used in GA4 to create a custom event goal for Google Ads
      project_number: (existingProjectsData?.total ?? 0) + 1, // number of projects in the organization
      paywall_flow: paywallFlow,
    };
    trackEvent(AnalyticsEvents.ProjectStart, eventProps);

    return data.data;
  };

  return {
    createProject,
    isCreating,
    organization,
    workspaceId,
    trackEvent,
    paywallFlow,
  };
};
